import { QueryClient } from 'react-query';
import { allNxHubDevicesQueryKey } from 'web/services/api/nxHubQueryKeys';

// React Query cache bookkeeping for the Hypercare portal mutations, extracted so the
// coherency rules are unit-testable without rendering hooks (see
// __tests__/hypercareCache.test.ts). The subtlety these encode (PR #654 review): the
// lazily-fetched webhook URL lives under its own per-account key, so invalidating the
// accounts list alone leaves a rotated/deleted URL readable from cache — e.g. "Show URL"
// after a remount would display (and let the user copy) the dead URL until a refetch.

export const hypercareAccountsKey = (organizationId: string) => ['hypercare-accounts', organizationId];
export const hypercareAssignmentsKey = (organizationId: string) => ['hypercare-assignments', organizationId];
export const hypercareWebhookUrlKey = (accountId: string) => ['hypercare-webhook-url', accountId];
export const hypercareTtsSettingsKey = (organizationId: string) => ['hypercare-tts-settings', organizationId];

/** Mint or rotate: the response IS the new URL — seed the per-account URL cache with it so
 * no stale (now dead) URL can ever be served, even across remounts. Only the URL is seeded:
 * the mint response also carries one-shot registration-status fields that don't belong in
 * the GET-shaped cache entry. */
export const applyHypercareWebhookMintToCache = (
    queryClient: QueryClient,
    accountId: string,
    organizationId: string,
    data: { webhookUrl: string }
): void => {
    queryClient.setQueryData(hypercareWebhookUrlKey(accountId), { webhookUrl: data.webhookUrl });
    queryClient.invalidateQueries(hypercareAccountsKey(organizationId));
};

/** Token revoked: the URL no longer exists server-side (the read endpoint 404s) — drop the
 * cache entry entirely rather than leaving it to be displayed or copied. */
export const applyHypercareWebhookDeleteToCache = (
    queryClient: QueryClient,
    accountId: string,
    organizationId: string
): void => {
    queryClient.removeQueries(hypercareWebhookUrlKey(accountId));
    queryClient.invalidateQueries(hypercareAccountsKey(organizationId));
};

/** Org announcement voice saved (204, no body): write the new value straight into the settings
 * cache. Invalidate-and-refetch leaves a stale window in which the select snaps back to the old
 * voice and Preview posts it (PR #670 review); the write-through closes it. '' clears back to
 * the default (stored as null, matching the GET shape). */
export const applyHypercareOrgTtsVoiceToCache = (
    queryClient: QueryClient,
    organizationId: string,
    voice: string
): void => {
    const key = hypercareTtsSettingsKey(organizationId);
    const prev = queryClient.getQueryData<{ orgVoice: string | null }>(key);
    if (prev) {
        queryClient.setQueryData(key, { ...prev, orgVoice: voice === '' ? null : voice });
    }
};

/** Device voice override saved: patch the one device row inside the aggregated fleet query (the
 * table reads nxHubTtsVoice off it) — same stale-window reasoning as the org voice, and cheaper
 * than refetching the whole fleet. */
export const applyHypercareDeviceTtsVoiceToCache = (
    queryClient: QueryClient,
    organizationId: string,
    deviceId: string,
    voice: string
): void => {
    const key = allNxHubDevicesQueryKey(organizationId);
    const prev = queryClient.getQueryData<{ devices: { id: string; nxHubTtsVoice?: string | null }[] }>(key);
    if (prev) {
        queryClient.setQueryData(key, {
            ...prev,
            devices: prev.devices.map((d) =>
                d.id === deviceId ? { ...d, nxHubTtsVoice: voice === '' ? null : voice } : d
            ),
        });
    }
};

/** Account deleted (the server refuses while devices are still assigned to it): refetch the
 * accounts list, drop the account's URL, and refetch assignments too — another session may
 * have reassigned devices concurrently, and the devices table reads from a separate key. */
export const applyHypercareAccountDeleteToCache = (
    queryClient: QueryClient,
    accountId: string,
    organizationId: string
): void => {
    queryClient.invalidateQueries(hypercareAccountsKey(organizationId));
    queryClient.invalidateQueries(hypercareAssignmentsKey(organizationId));
    queryClient.removeQueries(hypercareWebhookUrlKey(accountId));
};
